import moment from "moment";

import { GuidHelper } from "@/Common/Helpers/GuidHelper";
import { JSXHelper } from "@/Common/Helpers/JSXHelper";
import { Guid } from "@/Common/Models/Guid";
import * as LocalPatientOperations from "@/Common/Operations/operations.patient.local";
import { LocalEvent } from "@/Common/Stores/Actions/LocalEvent";
import { EncounterNote } from "@/Patient/Models/MedicalSummary/EncounterNote";

interface IMedicalNotePopupProps {
    ControlId?: string;
    NoteContext: string;
    Notes: Immutable.List<EncounterNote>;
    EncounterId: Guid;

    Target?: number;
    EventToDispatchOnSave: number;
    leftPosition: number;
    topPosition: number;
    OnResizeoperation?: number;
}

class MedicalNotePopupState {
    constructor(s?: MedicalNotePopupState) {
        if (s != null) {
            this.note = s.note;
        }
        else {
            this.note = "";
        }
    }

    public note: string;
}

class MedicalNotePopup extends React.Component<IMedicalNotePopupProps, MedicalNotePopupState> {
    private controlId: string;
    private sending: boolean;

    constructor(props: IMedicalNotePopupProps) {
        super(props);

        this.controlId = props.ControlId == null ? GuidHelper.NewGuid() : props.ControlId;

        this.state = new MedicalNotePopupState();
        this.sending = false;
    }

    public render(): JSX.Element {
        var self = this;
        var style = {
            position: "absolute",
            left: self.props.leftPosition + "px",
            top: self.props.topPosition + "px"
        } as React.CSSProperties;
        var isDisabled = this.state.note.trim().length === 0 || this.sending == true;

        var title = this.GetTitle(this.props.NoteContext);
        var notesToDisplay = this.GetNotesToDisplay(this.props.NoteContext, this.props.Notes.toArray());

        return (
            <div
                id={this.controlId}
                data-cy="medicalNotePopup"
                onClick={this.CancelClickPropagation}
                className="notes-modal"
                style={style}
                aria-autoclose="false"
            >
                <div className="notes-dropdown-content">
                    <h4> {title} : {cacheStore.GetString("NotesLabel")}</h4>

                    <div className="scroll-limit">
                        {notesToDisplay.map((note: EncounterNote, idx: number) => {
                            var person = cacheStore.GetEmployeeByContactFacetId(note.EmployeeId);
                            var docPrefix = person.PrefixLookup.GetValue();
                            return (
                                <div key={note.Id + "_" + idx} className="notes-collection-item">
                                    <div className="memo-header"><strong>{docPrefix} {person.LastName}, {person.FirstName}</strong> <span> {moment(note.DateAdded).format("YYYY/MM/DD ") + " " + cacheStore.GetString("AtLabel") + " " + moment(note.DateAdded).format("HH:mm:ss")}</span></div>
                                    <div className="note-container">
                                        {JSXHelper.BreakStringIntoParagraphs(note.Note)}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                    <textarea
                        className="form-control"
                        value={this.state.note}
                        id="memo_doctb"
                        data-cy="medicalNotePopup-note"
                        onChange={this.OnTexboxChange.bind(this)}
                        rows={4}
                    />
                </div>
                <div className="notes-content-footer text-right">
                    <button className="btn btn-default push-right-5" onClick={this.CancelChanges.bind(this)}>{cacheStore.GetString("CancelLabel")}</button>
                    <button className="btn btn-primary" disabled={isDisabled} onClick={this.SubmitNote.bind(this)}>{cacheStore.GetString("NoteSubmitLabel")}</button>
                </div>
            </div>
        );
    }

    public componentDidMount(): void {
        window.addEventListener("resize", this.HandleResize);
        var elem = document.getElementById("memo_doctb");
        autosize(elem);
    }

    public componentWillUnmount(): void {
        window.removeEventListener("resize", this.HandleResize);
        var elem = document.getElementById("memo_doctb");
        var event = document.createEvent("Event");
        event.initEvent("autosize:destroy", true, false);
        elem.dispatchEvent(event);
    }

    public shouldComponentUpdate(nextProp: IMedicalNotePopupProps, nextState: MedicalNotePopupState): boolean {
        var subTarget_mismatch = this.props.Target !== nextProp.Target;
        var position = this.props.leftPosition !== nextProp.leftPosition || this.props.topPosition !== nextProp.topPosition;
        return position || subTarget_mismatch || this.HasStateChanged(nextState) || this.props.Notes !== nextProp.Notes;
    }

    public SubmitNote(): void {
        if (this.sending === false) {
            this.sending = true;
            var operation = new LocalEvent(this.props.EventToDispatchOnSave);

            operation.AddItem("context", this.props.NoteContext);
            operation.AddItem("encounterId", this.props.EncounterId);
            operation.AddItem("noteText", this.state.note);
            operation.AddItem("employeeContactFacerIt", sessionStore.getCurrentEmployee().ContactFacetId);
            LocalEventDispatcher.dispatch(operation);
        }
    }

    private CancelClickPropagation(e: React.MouseEvent): void {
        e.stopPropagation();
    }

    private OnTexboxChange(e: React.ChangeEvent<HTMLTextAreaElement>): void {
        var s = new MedicalNotePopupState(this.state);
        s.note = e.target.value;
        this.setState(s);
    }

    private HasStateChanged(nextState: MedicalNotePopupState): boolean {
        var b = this.state.note !== nextState.note;
        return b;
    }

    private HandleResize(): void {
        if (this.props.OnResizeoperation != null) {
            var operation = new LocalEvent(this.props.OnResizeoperation);
            LocalEventDispatcher.dispatch(operation);
        }
    }

    private GetNotesToDisplay(noteContext: string, allNotes: EncounterNote[]): EncounterNote[] {
        var result: EncounterNote[] = [];

        switch (noteContext) {
            case "EXAM":
                result = allNotes.filter(x => x.Context == "EXAM");
                break;
            case "HPI":
                result = allNotes.filter(x => x.Context == "HPI");
                break;
            case "IMG":
                result = allNotes.filter(x => x.Context == "IMG");
                break;
            case "LAB":
                result = allNotes.filter(x => x.Context == "LAB");
                break;
            case "OTH":
                result = allNotes.filter(x => x.Context == "OTH");
                break;
            case "OTHN":
                result = allNotes.filter(x => x.Context == "OTHN");
                break;
            case "RFV":
                result = allNotes.filter(x => x.Context == "RFV");
                break;

            default:
                break;
        }

        return result;
    }

    private GetTitle(noteContext: string): string {
        var result = "";

        switch (noteContext) {
            case "EXAM":
                result = cacheStore.GetString("ExamLabel");
                break;
            case "HPI":
                result = cacheStore.GetString("HPILabel");
                break;
            case "IMG":
                result = cacheStore.GetString("RadiologyLabel");
                break;
            case "LAB":
                result = cacheStore.GetString("LaboratoryLabel");
                break;
            case "OTH":
                result = cacheStore.GetString("OtherPlanNoteLbl");
                break;
            case "OTHN":
                result = cacheStore.GetString("OtherNotesAndInterventionsLabel");
                break;
            case "RFV":
                result = cacheStore.GetString("VisitPurposeMLabel");
                break;

            default:
                break;
        }

        return result;
    }

    private CancelChanges(): void {
        LocalEvent.Dispatch(LocalPatientOperations.MedicalNote.CloseNotePopup);
    }
}

export { MedicalNotePopup };
